import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { AngularFireDatabase, FirebaseListObservable, FirebaseObjectObservable } from 'angularfire2/database';
import { DatabaseReference, DatabaseSnapshot, Query } from 'angularfire2/database/interfaces';
import { storage } from 'firebase';
import { AngularFireAuth } from 'angularfire2/auth';

import 'rxjs/add/operator/map';

import { Event } from '../models/event';
import { User } from '../models/user';
import { EventFeature } from '../models/event-feature';

import { FormValues } from '../events/events-list.component';
import { FormValues as FormValuesUpdate } from '../events/info/info-update.component';
import { FormValues as FormValuesAppDesign } from '../events/app-design/app-design-list.component';

import * as moment from 'moment';

import { EventFeatureService } from '../services/event-feature.service';

@Injectable()
export class EventService {

  private databaseReference: DatabaseReference;
  private storageReference: storage.Reference;

  constructor(
    private afAuth: AngularFireAuth,
    private afDatabase: AngularFireDatabase,
    private eventFeatureService: EventFeatureService
  ) {
    this.databaseReference = afDatabase.database.ref('events');
    this.storageReference = storage().ref('events');

  }

  async getActivityAreaImageURLOfEvent(event: Event): Promise<URL | null> {
    const urlString: string = await this.storageReference
      .child(event.id)
      .child('activity-area.jpg')
      .getDownloadURL();

    return new URL(urlString);
  }

  async removeEventActivityMap(event: Event) {
    try {
      await this.storageReference.child(event.id).child('activity-area.jpg').delete();
    } catch (error) {
      throw Error(error.message);
    }
  }

  async getLogoImageURLOfEvent(event: Event): Promise<URL | null> {
    const urlString: string = await this.storageReference
      .child(event.id)
      .child('logo.jpg')
      .getDownloadURL();

    return new URL(urlString);
  }

  async getBannerImageURLOfEvent(event: Event): Promise<URL | null> {
    const urlString: string = await this.storageReference
      .child(event.id)
      .child('banner.jpg')
      .getDownloadURL();

    return new URL(urlString);
  }

  getEvents(query?: Query): FirebaseListObservable<Event[]> {
    return this.afDatabase.list(this.databaseReference, { preserveSnapshot: true, query: query })
      .map((eventsSnapshot: DatabaseSnapshot) => {
        const events: Event[] = [];
        const eventSnapshots: DatabaseSnapshot[] = [];

        eventsSnapshot.forEach(eventSnapshot => {
          if (eventSnapshot.exists()) {
            eventSnapshots.push(eventSnapshot);
          }

          return false;
        });

        for (const eventSnapshot of eventSnapshots) {
          const event = Event.fromDatabaseSnapshot(eventSnapshot);

          if (event) {
            events.push(event);
          }
        }

        return events;
      }) as FirebaseListObservable<Event[]>;
  }

  getEvent(id: string): FirebaseObjectObservable<Event | null> {
    const databaseReference = this.databaseReference.child(id);
    return this.afDatabase.object(databaseReference, { preserveSnapshot: true })
      .map((eventSnapshot: DatabaseSnapshot) =>
        Event.fromDatabaseSnapshot(eventSnapshot)
      ) as FirebaseObjectObservable<Event | null>;
  }

  async setEvent(formValues: FormValues, companyId: string): Promise<string> {
    if (!formValues.name || !formValues.startTimestamp || !formValues.endTimestamp || !companyId) {
      throw Error('Form is incomplete.');
    }
    if (formValues.selectedFeatures.length < 1) {
      throw Error('You must select atleast one feature.');
    }

    const eventObject: Object = {};

    eventObject[Event.PropertyKey.name] = formValues.name;
    eventObject[Event.PropertyKey.startTimestamp] = moment(formValues.startTimestamp).valueOf();
    eventObject[Event.PropertyKey.endTimestamp] = moment(formValues.endTimestamp).valueOf();
    eventObject[Event.PropertyKey.companyId] = companyId;

    //Check if contains feedback form feature
    if (formValues.selectedFeatures.includes('Feedback Form')) {
      eventObject[Event.PropertyKey.isShowFeedbackForm] = true;
    } else {
      eventObject[Event.PropertyKey.isShowFeedbackForm] = false;
    }

    const keywordObjectIds: Object = {};


    const databaseReference = await this.getEvents().push(eventObject);

    await this.eventFeatureService.setEventFeatures(databaseReference.key, formValues.selectedFeatures);

    await this.setEventDays(databaseReference.key, moment(formValues.startTimestamp), moment(formValues.endTimestamp));

    return databaseReference.key;
  }

  updateEvent(companyId: string, event$: FirebaseObjectObservable<Event | null>, formValues: FormValuesUpdate, arrayDays: Day[]): Promise<string> {
    return new Promise((resolve, reject) => {
      event$
        .first()
        .subscribe(async (event: Event | null) => {

          if (!this.afAuth.auth.currentUser) {
            reject(Error('User is not logged in.'));
            return;
          }

          if (!event) {
            return;
          }

          if (!formValues.name || !formValues.startTimestamp || !formValues.endTimestamp || !companyId) {
            throw Error('Form is incomplete.');
          }

          if (!formValues.latitude || !formValues.longitude) {
            reject(Error('Longitude and Latitude required'));
          }

          const eventObject: Object = {};

          eventObject[Event.PropertyKey.name] = formValues.name;
          eventObject[Event.PropertyKey.startTimestamp] = moment(formValues.startTimestamp).valueOf();
          eventObject[Event.PropertyKey.endTimestamp] = moment(formValues.endTimestamp).valueOf();
          eventObject[Event.PropertyKey.companyId] = companyId;
          eventObject[Event.PropertyKey.facebookId] = formValues.facebookId;
          eventObject[Event.PropertyKey.twitterScreenName] = formValues.twitterScreenName;
          eventObject[Event.PropertyKey.instagramUsername] = formValues.instagramUsername;
          eventObject[Event.PropertyKey.linkedinId] = formValues.linkedinId;
          eventObject[Event.PropertyKey.description] = formValues.description;
          eventObject[Event.PropertyKey.address] = formValues.address;
          eventObject[Event.PropertyKey.latitude] = formValues.latitude;
          eventObject[Event.PropertyKey.longitude] = formValues.longitude;
          eventObject[Event.PropertyKey.tagLine] = formValues.tagLine;
          eventObject[Event.PropertyKey.hashTag] = formValues.hashTag;
          eventObject[Event.PropertyKey.appStoreUrl] = formValues.appStoreUrl;
          eventObject[Event.PropertyKey.playStoreUrl] = formValues.playStoreUrl;
          eventObject[Event.PropertyKey.apkDownloadUrl] = formValues.apkDownloadUrl;

          await this.updateEventDays(event.id, arrayDays);

          try {

            if (formValues.activityMapImage) {
              await storage()
                // .ref(`events/${event.id}/activity-area.jpg`)
                .ref(`users/${this.afAuth.auth.currentUser.uid}/uploads/events/${event.id}/activity-area.jpg`)
                .put(formValues.activityMapImage);
            }

            if (formValues.logoImage) {
              await storage()
                .ref(`users/${this.afAuth.auth.currentUser.uid}/uploads/events/${event.id}/logo.jpg`)
                .put(formValues.logoImage);
            }

            if (formValues.bannerImage) {
              await storage()
                .ref(`users/${this.afAuth.auth.currentUser.uid}/uploads/events/${event.id}/banner.jpg`)
                .put(formValues.bannerImage);
            }

            await event$.update(eventObject);
            resolve(event.id);
          } catch (error) {
            reject(error);
          }
        });
    });
  }

  updateEventAppDesign(event$: FirebaseObjectObservable<Event | null>, formValues: FormValuesAppDesign): Promise<string> {
    return new Promise((resolve, reject) => {
      event$
        .first()
        .subscribe(async (event: Event | null) => {
          if (!event) {
            return;
          }

          if (!formValues.primaryColor || !formValues.accentColor) {
            reject(Error('Form Incomplete.'));
          }

          const appDesignObject: Object = {};

          appDesignObject[Event.PropertyKey.appPrimaryColor] = formValues.primaryColor;
          appDesignObject[Event.PropertyKey.appAccentColor] = formValues.accentColor;

          try {
            await event$.update(appDesignObject);
            resolve(event.id);
          } catch (error) {
            reject(error);
          }
        });
    });
  }

  updateEventFeaturesOrder(event$: FirebaseObjectObservable<Event | null>, eventFeatures: EventFeature[]): Promise<string> {
    return new Promise((resolve, reject) => {
      event$
        .first()
        .subscribe(async (event: Event | null) => {
          if (!event) {
            return;
          }

          if (eventFeatures.length === 0) {
            reject(Error('No event features.'));
          }

          for (const feature of eventFeatures) {

            const featureObject: Object = {};

            featureObject[EventFeature.PropertyKey.icon] = feature.icon;
            featureObject[EventFeature.PropertyKey.name] = feature.name;
            featureObject[EventFeature.PropertyKey.order] = feature.order;

            try {
              await this.afDatabase.database
                .ref(`event-features/${event.id}/${feature.id}`)
                .update(featureObject);
              resolve(event.id);
            } catch (error) {
              reject(error);
            }


          }




        });
    });
  }

  getListofDays (startTime, endTime) {
    // Determine days between range
    console.log(`INPUTS: ${startTime}   |   ${endTime}`);
    var dateArray : String[] = [];
    while (startTime <= endTime) {
        dateArray.push(moment(startTime).format('MMM DD')) // change to appropriate format
        startTime = moment(startTime).add(1, 'days');
    }
    console.log(`DateArray: ${dateArray}`);
    return dateArray;
  }

  setEventDays(eventId, startTime, endTime) {
    const dateArray = this.getListofDays(startTime, endTime);

    var dataObject = {};
    for (var i = 0; i < dateArray.length; i++) {
      dataObject[dateArray[i].toString()] = true;
    }
    const dbRef = this.afDatabase.database.ref('event-days').child(eventId);
    dbRef.set(dataObject).catch((er) => alert(`Failed saving event days: ${er}`));
  }

  initEventDays(eventId) {
    const databaseReference = this.afDatabase.database.ref('event-days').child(eventId);
    return databaseReference.once('value', (snapshot) => {
    return snapshot.val();
    });
  }

  async updateEventDays(eventId, arrayDays) {
    const dbRef = this.afDatabase.database.ref('event-days').child(eventId);
    var dataObject = {};
    for (var i = 0; i < arrayDays.length; i++) {
      dataObject[arrayDays[i]['date']] = arrayDays[i]['isShown'];
    }
    try {
      await dbRef.remove();
      await dbRef.set(dataObject);
    } catch (er){
      alert(`Failed saving event days: ${er}`);
    }
  }
}

export interface Day {
  date: String | null;
  isShown?: boolean | true;
}




// WEBPACK FOOTER //
// D:/ThinkBIT/work-projects/confe/hosting/src/app/services/event.service.ts